import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, BookMarked, XCircle, CheckCircle2, Send, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api, { billBookAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import BranchSelect from '../components/BranchSelect';

const STATUS_CLS = {
  active: 'bg-emerald-50 text-emerald-600', approved: 'bg-blue-50 text-blue-600',
  completed: 'bg-slate-100 text-slate-500', closed: 'bg-rose-50 text-rose-500',
  pending: 'bg-amber-50 text-amber-600', rejected: 'bg-rose-50 text-rose-500',
};
const Badge = ({ v }) => <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${STATUS_CLS[v] || 'bg-slate-100 text-slate-500'}`}>{v === 'approved' ? 'queued' : v}</span>;

export default function BillBooksPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'owner', 'super_admin'].includes(user?.role);
  const [dash, setDash] = useState(null);
  const [books, setBooks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [approving, setApproving] = useState(null);

  const load = useCallback(() => {
    billBookAPI.dashboard().then(r => setDash(r.data)).catch(() => {});
    billBookAPI.list().then(r => setBooks(r.data?.bill_books || [])).catch(() => toast.error('Failed to load bill books'));
    billBookAPI.requests().then(r => setRequests(r.data?.requests || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const closeBook = async (id) => {
    if (!window.confirm('Close this Bill Book? Its remaining numbers become unusable.')) return;
    try { await billBookAPI.close(id); toast.success('Bill Book closed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const reject = async (r) => {
    const remarks = window.prompt('Rejection remarks (optional):') || '';
    try { await billBookAPI.rejectRequest(r.request_id, { remarks }); toast.success('Request rejected'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const cards = dash?.cards || {};

  return (
    <div data-testid="bill-books-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2"><BookMarked className="w-6 h-6 text-purple-600" /> Bill Books</h1>
          <p className="text-sm text-slate-500 mt-1">LR & Invoice number ranges per branch. All document numbers come from Bill Books.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-secondary flex items-center gap-1.5 text-sm" data-testid="refresh-bill-books"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <Button variant="outline" onClick={() => setShowRequest(true)} data-testid="request-bill-book-btn"><Send className="w-4 h-4 mr-1" /> Request New Book</Button>
          {isAdmin && <Button onClick={() => setShowCreate(true)} data-testid="create-bill-book-btn"><Plus className="w-4 h-4 mr-1" /> New Bill Book</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[['Total Books', cards.total_books, 'bb-card-total'], ['Active', cards.active, 'bb-card-active'],
          ['Queued', cards.queued, 'bb-card-queued'], ['Completed', cards.completed, 'bb-card-completed'],
          ['Pending Requests', cards.pending_requests, 'bb-card-pending_requests'], ['Numbers Left', cards.remaining_numbers, 'bb-card-remaining']]
          .map(([l, v, tid]) => (
          <div key={l} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-4" data-testid={tid}>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">{l}</p>
            <p className="text-xl font-mono font-bold text-slate-900 dark:text-slate-100 mt-1">{v ?? '—'}</p>
          </div>
        ))}
      </div>

      {dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Monthly Usage</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dash.monthly_usage}>
                <XAxis dataKey="month" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Legend />
                <Bar dataKey="lr" name="LRs" fill="#9333ea" radius={[3, 3, 0, 0]} />
                <Bar dataKey="invoice" name="Invoices" fill="#059669" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-4">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Branch-wise Usage</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={dash.branch_usage}>
                <XAxis dataKey="branch" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Legend />
                <Bar dataKey="lr_used" name="LR used" fill="#9333ea" radius={[3, 3, 0, 0]} />
                <Bar dataKey="invoice_used" name="Invoice used" fill="#059669" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        <div className="px-4 pt-4 pb-2"><h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Bill Books</h3></div>
        <table className="w-full data-table">
          <thead><tr><th>Name</th><th>Branch</th><th>Type</th><th>Prefix</th><th>Range</th><th>Next No.</th><th>Used / Remaining</th><th>Status</th>{isAdmin && <th className="text-right">Actions</th>}</tr></thead>
          <tbody>
            {books.length === 0 ? <tr><td colSpan="9" className="text-center py-8 text-slate-400">No bill books yet.</td></tr>
            : books.map(b => {
              const pct = b.total_numbers ? Math.round((b.used_numbers / b.total_numbers) * 100) : 0;
              return (
                <tr key={b.bill_book_id} data-testid={`bill-book-row-${b.bill_book_id}`}>
                  <td className="font-medium">{b.name}</td>
                  <td>{b.branch_name} <span className="text-[10px] text-slate-400 font-mono">({b.branch_code})</span></td>
                  <td className="uppercase text-xs font-semibold">{b.book_type}</td>
                  <td className="font-mono text-xs">{b.prefix || '—'}</td>
                  <td className="font-mono text-xs">{b.start_number} – {b.end_number}</td>
                  <td className="font-mono text-xs">{b.status === 'active' ? b.current_number : '—'}</td>
                  <td className="min-w-[130px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden"><div className={`h-full ${b.remaining_numbers <= 25 && b.status === 'active' ? 'bg-rose-500' : 'bg-purple-500'}`} style={{ width: `${pct}%` }} /></div>
                      <span className="text-[10px] font-mono text-slate-500">{b.used_numbers}/{b.remaining_numbers} left</span>
                    </div>
                  </td>
                  <td><Badge v={b.status} /></td>
                  {isAdmin && <td className="text-right">
                    {['active', 'approved'].includes(b.status) && <button onClick={() => closeBook(b.bill_book_id)} className="p-1.5 hover:bg-rose-50 rounded" title="Close book" data-testid={`close-book-${b.bill_book_id}`}><XCircle className="w-4 h-4 text-rose-500" /></button>}
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        <div className="px-4 pt-4 pb-2"><h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Bill Book Requests</h3></div>
        <table className="w-full data-table">
          <thead><tr><th>Date</th><th>Branch</th><th>Type</th><th>Requested By</th><th>Current Book</th><th>Last No.</th><th>Reason</th><th>Status</th>{isAdmin && <th className="text-right">Actions</th>}</tr></thead>
          <tbody>
            {requests.length === 0 ? <tr><td colSpan="9" className="text-center py-8 text-slate-400">No requests.</td></tr>
            : requests.map(r => (
              <tr key={r.request_id} data-testid={`bb-request-row-${r.request_id}`}>
                <td className="text-xs">{(r.created_at || '').split('T')[0]}</td>
                <td>{r.branch_name}</td>
                <td className="uppercase text-xs font-semibold">{r.book_type}</td>
                <td>{r.requested_by_name}</td>
                <td className="text-xs">{r.current_book_name || '—'}</td>
                <td className="font-mono text-xs">{r.current_last_number || '—'}</td>
                <td className="text-xs max-w-[180px] truncate" title={r.reason}>{r.reason || '—'}</td>
                <td><Badge v={r.status} /></td>
                {isAdmin && <td className="text-right">
                  {r.status === 'pending' && (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setApproving(r)} className="p-1.5 hover:bg-emerald-50 rounded" title="Approve & allocate" data-testid={`approve-request-${r.request_id}`}><CheckCircle2 className="w-4 h-4 text-emerald-600" /></button>
                      <button onClick={() => reject(r)} className="p-1.5 hover:bg-rose-50 rounded" title="Reject" data-testid={`reject-request-${r.request_id}`}><XCircle className="w-4 h-4 text-rose-500" /></button>
                    </div>
                  )}
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <BookFormDialog onClose={() => setShowCreate(false)} onSaved={load} />}
      {showRequest && <RequestDialog onClose={() => setShowRequest(false)} onSaved={load} />}
      {approving && <ApproveDialog req={approving} onClose={() => setApproving(null)} onSaved={load} />}
    </div>
  );
}

function BookFormDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ branch_id: '', book_type: 'lr', name: '', prefix: '', start_number: '', end_number: '' });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const save = async () => {
    if (!form.branch_id) { toast.error('Select a branch'); return; }
    if (!form.start_number || !form.end_number) { toast.error('Enter number range'); return; }
    setSaving(true);
    try {
      const res = await billBookAPI.create(form);
      toast.success(`Bill Book created (${res.data.status === 'approved' ? 'queued — activates automatically when current book ends' : 'active'})`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bill-book-form-modal">
        <DialogHeader><DialogTitle>New Bill Book</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="form-label">Branch *</label><BranchSelect value={form.branch_id} onChange={v => u('branch_id', v)} testId="bb-branch-select" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label">Type *</label><select className="form-input" value={form.book_type} onChange={e => u('book_type', e.target.value)} data-testid="bb-type-select"><option value="lr">LR</option><option value="invoice">Invoice</option></select></div>
            <div><label className="form-label">Name</label><input className="form-input" value={form.name} onChange={e => u('name', e.target.value)} placeholder="Delhi LR Book 2" /></div>
          </div>
          <div><label className="form-label">Prefix</label><input className="form-input font-mono" value={form.prefix} onChange={e => u('prefix', e.target.value)} placeholder="DL001-" data-testid="bb-prefix-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label">Start Number *</label><input type="number" className="form-input font-mono" value={form.start_number} onChange={e => u('start_number', e.target.value)} placeholder="100001" data-testid="bb-start-input" /></div>
            <div><label className="form-label">End Number *</label><input type="number" className="form-input font-mono" value={form.end_number} onChange={e => u('end_number', e.target.value)} placeholder="100500" data-testid="bb-end-input" /></div>
          </div>
          {form.prefix && form.start_number && <p className="text-xs text-slate-500">Numbers will look like: <span className="font-mono font-semibold text-purple-600">{form.prefix}{String(form.start_number).padStart(String(form.end_number || form.start_number).length, '0')}</span></p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-bill-book-btn">{saving ? 'Saving...' : 'Create Bill Book'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ branch_id: '', book_type: 'lr', reason: '', remarks: '' });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await billBookAPI.createRequest(form); toast.success('Request sent to Admin'); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bb-request-modal">
        <DialogHeader><DialogTitle>Request New Bill Book</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="form-label">Branch</label><BranchSelect value={form.branch_id} onChange={v => u('branch_id', v)} testId="bbr-branch-select" /></div>
          <div><label className="form-label">Type</label><select className="form-input" value={form.book_type} onChange={e => u('book_type', e.target.value)} data-testid="bbr-type-select"><option value="lr">LR</option><option value="invoice">Invoice</option></select></div>
          <div><label className="form-label">Reason</label><input className="form-input" value={form.reason} onChange={e => u('reason', e.target.value)} placeholder="Current book exhausted" data-testid="bbr-reason-input" /></div>
          <div><label className="form-label">Remarks</label><textarea className="form-input min-h-[50px]" value={form.remarks} onChange={e => u('remarks', e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="send-bb-request-btn">{saving ? 'Sending...' : 'Send Request'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveDialog({ req, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', prefix: '', start_number: req.current_last_number ? req.current_last_number + 1 : '', end_number: '', remarks: '' });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const save = async () => {
    if (!form.start_number || !form.end_number) { toast.error('Enter number range'); return; }
    setSaving(true);
    try { await billBookAPI.approveRequest(req.request_id, form); toast.success('Request approved — Bill Book allocated'); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bb-approve-modal">
        <DialogHeader><DialogTitle>Approve & Allocate — {req.book_type.toUpperCase()} for {req.branch_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Current book: <span className="font-mono">{req.current_book_name || '—'}</span> · Last used number: <span className="font-mono font-semibold">{req.current_last_number || '—'}</span></p>
          <div><label className="form-label">Book Name</label><input className="form-input" value={form.name} onChange={e => u('name', e.target.value)} /></div>
          <div><label className="form-label">Prefix</label><input className="form-input font-mono" value={form.prefix} onChange={e => u('prefix', e.target.value)} data-testid="bba-prefix-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label">Start Number *</label><input type="number" className="form-input font-mono" value={form.start_number} onChange={e => u('start_number', e.target.value)} data-testid="bba-start-input" /></div>
            <div><label className="form-label">End Number *</label><input type="number" className="form-input font-mono" value={form.end_number} onChange={e => u('end_number', e.target.value)} data-testid="bba-end-input" /></div>
          </div>
          <div><label className="form-label">Remarks</label><input className="form-input" value={form.remarks} onChange={e => u('remarks', e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="approve-allocate-btn">{saving ? 'Allocating...' : 'Approve & Allocate'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
